import { useState } from "react";
import { api, formatUSD } from "../lib/api";
import { Input } from "../components/ui/input";
import { Package, Download } from "lucide-react";

const STATUS_COLOR = {
  pending_fulfillment: "text-amber-glow",
  in_production: "text-amber-glow",
  shipped: "text-emerald-400",
  delivered: "text-emerald-400",
  awaiting_payment: "text-neutral-500",
};

export default function Account() {
  const [email, setEmail] = useState("");
  const [orders, setOrders] = useState(null);
  const [loading, setLoading] = useState(false);

  const lookup = async (e) => {
    e.preventDefault();
    if (!email) return;
    setLoading(true);
    const { data } = await api.get("/orders/lookup", { params: { email } });
    setOrders(data);
    setLoading(false);
  };

  return (
    <div className="max-w-3xl mx-auto px-6 lg:px-10 pt-28 pb-24">
      <div className="overline-label mb-3">Order history & downloads</div>
      <h1 className="font-serif text-5xl lg:text-6xl font-light">My Orders</h1>
      <p className="mt-4 text-neutral-400">Enter the email you used at checkout to view orders and download your licenses.</p>

      <form onSubmit={lookup} className="mt-8 flex gap-3">
        <Input data-testid="account-email" placeholder="you@example.com" value={email} onChange={(e) => setEmail(e.target.value)} />
        <button data-testid="account-lookup" className="btn-amber rounded-full px-6 whitespace-nowrap">{loading ? "…" : "Find orders"}</button>
      </form>

      {orders && (
        <div className="mt-10 space-y-4" data-testid="account-orders">
          {orders.length === 0 && <div className="text-neutral-500 py-10 text-center">No orders found for that email.</div>}
          {orders.map((o) => (
            <div key={o.id} className="border border-white/10 rounded-lg p-5 bg-obsidian-800">
              <div className="flex items-start justify-between">
                <div>
                  <div className="font-mono text-xs text-neutral-500">{o.order_number}</div>
                  <div className="font-serif text-xl mt-1">{o.source_title}</div>
                  <div className="text-sm text-neutral-400 mt-1 capitalize">
                    {o.type.replace("_", " ")} {o.print ? `· ${o.print.size}` : o.license_type ? `· ${o.license_type}` : ""}
                  </div>
                </div>
                <div className="text-right">
                  <div className="font-mono text-amber-glow">{formatUSD(o.total)}</div>
                  <div className={`text-xs mt-1 flex items-center gap-1 justify-end ${STATUS_COLOR[o.fulfillment_status] || "text-neutral-400"}`}>
                    <Package size={12} /> {(o.fulfillment_status || "").replace(/_/g, " ")}
                  </div>
                </div>
              </div>
              {o.millers_order_id && <div className="mt-3 font-mono text-[0.65rem] text-neutral-500">Miller's #{o.millers_order_id}</div>}
              {o.type === "license" && o.payment_status === "paid" && o.download_url && (
                <a href={o.download_url.startsWith("http") ? o.download_url : `${process.env.REACT_APP_BACKEND_URL}${o.download_url}`}
                  target="_blank" rel="noreferrer" download
                  className="mt-3 inline-flex items-center gap-2 text-sm text-amber-glow link-underline" data-testid={`download-${o.id}`}>
                  <Download size={14} /> Download license file
                </a>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
